/**
 * The AI-breakdown tier picker (SPEC.md §6.2, §11 item 9b) — sits beside every manual
 * "Break down" control (`TaskList.tsx`'s Split icon, `Subtasks.tsx`'s "+ add a step" and its
 * own per-subtask Split icon), since the two are separate actions: manual entry makes zero
 * model calls and stays instant (§6.1), this one asks a model and takes a moment.
 *
 * At the parent's own top-level trigger and each subtask's own Split, this component's
 * mount is itself gated on that row's manual Split having been pressed (`breakingDown` in
 * `TaskList.tsx`/`Subtasks.tsx`) — two competing "break this down" buttons sitting on every
 * row all the time was more chrome than the row needed before the owner had said they wanted
 * to. The fold's "+ add a step" is the one place it's shown unconditionally, since there's no
 * single manual trigger there to hide behind — "add a step" already *is* the pressed state.
 *
 * A `SplitButton` wearing a lightbulb instead of text: the main face is the *selected* tier
 * (the highest currently available one, until the owner picks a different one from the
 * flyout), lit to match (off/dim/on/bright — `icons.tsx::Lightbulb`). The chevron flies out
 * to Dim/On/Bright explicitly, each with its own lightbulb and showing why it's blocked when
 * it is (`aiTiers.ts`'s hint, not the server's real answer — see that module for why the gap
 * is fine). **Picking a tier from the flyout only selects it** — it changes what the main
 * face will run, the same way choosing a tier in a picker is a separate act from pressing
 * go; it does not itself call the model. Clicking the main face with nothing available still
 * sends the request rather than doing nothing — the server's own error
 * (`services/breakdown.py`) is the real, authoritative reason, so there is no separate
 * client-side "nothing to click" state to keep in sync with it.
 */

import { useId, useState } from "react";
import { type AiTierAvailability, TIER_BLURB, TIER_LABEL, TIER_ORDER } from "../aiTiers";
import { ApiError, type Task, type Tier } from "../api";
import { postNotice } from "../notices";
import { Lightbulb } from "./icons";
import { SplitButton, type SplitButtonItem } from "./SplitButton";

/** `T` is whatever the button was put on: a task, or a project (`ProjectOutlineButton`). */
export function AiBreakdownButton<T = Task>({
  task,
  tiers,
  onRun,
}: {
  task: T;
  tiers: AiTierAvailability;
  onRun: (task: T, tier: Tier) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  /** `null` until the owner explicitly picks something from the flyout — until then, the
   * main face tracks the highest currently-available tier rather than freezing on whatever
   * that happened to be at mount. */
  const [picked, setPicked] = useState<Tier | null>(null);
  const activeTier = picked ?? tiers.defaultTier;
  // A CSS anchor name has to be unique per instance, or every open flyout on the page fights
  // over the same anchor and `position-anchor` resolves to whichever one CSS's own tree-order
  // tie-break happens to pick — reported directly as "attaches to different places depending
  // on which is unfolded first." `useId()` is already unique per mount; `:` isn't a legal
  // character in a CSS custom-ident, so it's stripped rather than escaped.
  const anchorName = `--ai-tier-${useId().replace(/:/g, "")}`;

  if (!tiers.aiEnabled) return null;

  async function run(tier: Tier) {
    setBusy(true);
    try {
      await onRun(task, tier);
    } catch (err) {
      if (err instanceof ApiError) {
        postNotice(err.message);
        return;
      }
      throw err;
    } finally {
      setBusy(false);
    }
  }

  const items: SplitButtonItem[] = TIER_ORDER.map((tier) => ({
    key: tier,
    label: (
      <span className="ai-breakdown-menu-item">
        <Lightbulb level={tier} size={14} />
        {TIER_LABEL[tier]} - {tiers.available[tier] ? TIER_BLURB[tier] : tiers.blockedReason[tier]}
      </span>
    ),
    disabled: !tiers.available[tier],
    title: tiers.blockedReason[tier],
    onSelect: () => setPicked(tier),
  }));

  return (
    <span className="ai-breakdown-button">
      <SplitButton
        label={<Lightbulb level={busy ? "on" : (activeTier ?? "off")} size={14} />}
        mainAriaLabel={`Break down with AI${activeTier ? ` (${TIER_LABEL[activeTier]})` : ""}`}
        disabled={busy}
        onClick={() => void run(activeTier ?? "bright")}
        items={items}
        anchorName={anchorName}
      />
    </span>
  );
}
